import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import styled from "styled-components";

import { splitKeywordText } from "../KeywordChips";
import { normaliseKeywords } from "./selectionRules";

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 1rem 1.125rem;
  background: var(--surface-50);
  border: 1px solid var(--surface-200);
  border-radius: 10px;
`;

/** A "doesn't contain" word — the same red chip the rule cards use for one. */
export const NeverChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-family: monospace;
  font-size: 0.8125rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 6px;
  background: #fdecec;
  color: #7f1d1d;
`;

interface IIgnoreKeywordsBoxProps {
  value: string[];
  disabled?: boolean;
  onChange: (keywords: string[]) => void;
}

/**
 * CLE-2802 — "Always ignore lines containing": one account-wide list, applied before every rule and
 * every benefit. A word is added on Enter, on Add, or when the input loses focus, so typed text is
 * never left on screen looking saved.
 *
 * CLE-2895 — the text is split on commas and line breaks first, so pasting
 * "HOSPITAL, EMERGENCY,URGENT CARE" adds three words. A text input drops the line breaks of a paste
 * before anything here sees them, so a paste carrying any is caught and its breaks turned into commas.
 */
export function IgnoreKeywordsBox({
  value,
  disabled,
  onChange,
}: IIgnoreKeywordsBoxProps) {
  const [text, setText] = useState("");

  const commit = () => {
    const parts = splitKeywordText(text);
    if (parts.length === 0) return;
    onChange(normaliseKeywords([...value, ...parts]));
    setText("");
  };

  return (
    <Box data-testid="selectionRules.ignore">
      <div className="flex align-items-baseline gap-3 flex-wrap">
        <label htmlFor="selectionRules.ignore.add" className="font-semibold">
          Always ignore lines containing
        </label>
        <span className="text-xs text-color-secondary">
          Applied before every rule, to every benefit — including deductible,
          out-of-pocket and physician copay. Matched, ignoring case, anywhere in
          a line&apos;s messages, coverage description, PPO network name or
          place of service.
        </span>
      </div>
      <div className="flex flex-column gap-2">
        {value.map((keyword) => (
          <div
            key={keyword}
            className="flex align-items-center gap-2 flex-wrap"
            data-testid={`selectionRules.ignore.${keyword}`}
          >
            <NeverChip>
              {keyword}
              {!disabled && (
                <Button
                  type="button"
                  icon="pi pi-times"
                  className="p-button-text p-button-sm p-button-rounded p-0"
                  style={{ width: "1.25rem", height: "1.25rem" }}
                  aria-label={`Remove ${keyword}`}
                  data-testid={`selectionRules.ignore.${keyword}.remove`}
                  onClick={() =>
                    onChange(value.filter((word) => word !== keyword))
                  }
                />
              )}
            </NeverChip>
          </div>
        ))}
        <div className="flex align-items-center gap-2">
          <InputText
            id="selectionRules.ignore.add"
            data-testid="selectionRules.ignore.add"
            className="p-inputtext-sm"
            style={{ width: "24rem" }}
            placeholder="Add a word…"
            value={text}
            disabled={disabled}
            onChange={(event) => setText(event.target.value)}
            onPaste={(event) => {
              const pasted = event.clipboardData.getData("text");
              if (!/[\r\n]/.test(pasted)) return;
              event.preventDefault();
              const input = event.currentTarget;
              const start = input.selectionStart ?? text.length;
              const end = input.selectionEnd ?? text.length;
              setText(
                `${text.slice(0, start)}${pasted.replace(
                  /[\r\n]+/g,
                  ", "
                )}${text.slice(end)}`
              );
            }}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commit();
              }
            }}
          />
          <Button
            type="button"
            label="Add"
            className="p-button-outlined p-button-sm"
            disabled={disabled || !text.trim()}
            data-testid="selectionRules.ignore.addButton"
            onClick={commit}
          />
        </div>
      </div>
    </Box>
  );
}
